/** Pure, non-destructive recovery for drafts written by the old native forms. */
import {
  CppWizardFullSchema,
  EmergencyPlanWizardFullSchema,
  LightweightCppWizardFullSchema,
  RamsWizardFullSchema,
  SiteInductionWizardFullSchema,
  ToolboxTalkWizardFullSchema,
} from "../wizard-schemas";
import {
  GENERATION_DRAFT_VERSION,
} from "./schema";
import { RecoverableGenerationDraftV1Schema, type RecoverableGenerationDraftV1 } from "./native-draft";

export type RecoveryDocumentType =
  | "COSHH" | "RAMS" | "CPP" | "CPP_LIGHTWEIGHT"
  | "SITE_INDUCTION" | "EMERGENCY_PLAN" | "TOOLBOX_TALK";
export type DraftClassification = "canonical" | "legacy" | "mixed" | "unknown";
export type FieldDisposition = "exact-prefill" | "review-required" | "saved-notes-only";

const legacyKeys: Record<Exclude<RecoveryDocumentType, "COSHH">, readonly string[]> = {
  RAMS: Object.keys(RamsWizardFullSchema.shape),
  CPP: Object.keys(CppWizardFullSchema.shape),
  CPP_LIGHTWEIGHT: Object.keys(LightweightCppWizardFullSchema.shape),
  SITE_INDUCTION: Object.keys(SiteInductionWizardFullSchema.shape),
  EMERGENCY_PLAN: Object.keys(EmergencyPlanWizardFullSchema.shape),
  TOOLBOX_TALK: Object.keys(ToolboxTalkWizardFullSchema.shape),
};

/** Only values with an exact, independently checked web render destination. */
const exactLegacyFields: Partial<Record<RecoveryDocumentType, Readonly<Record<string, string>>>> = {
  SITE_INDUCTION: { reportingProcedures: "reportingProcedures" },
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function populated(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(populated);
  if (typeof value === "object") return Object.values(value).some(populated);
  return true;
}

/** RFC 6901 pointer segments keep literal dots/slashes distinct from nesting. */
function pointerSegment(key: string): string {
  return key.replace(/~/g, "~0").replace(/\//g, "~1");
}
function populatedFieldPaths(value: unknown, prefix: string): string[] {
  const object = record(value);
  if (object) {
    return Object.entries(object).flatMap(([key, child]) =>
      populatedFieldPaths(child, `${prefix}/${pointerSegment(key)}`));
  }
  return populated(value) ? [prefix] : [];
}

export interface RecoveredField {
  readonly sourceField: string;
  readonly disposition: FieldDisposition;
  readonly targetField?: string;
}

export interface GenerationDraftAnalysis {
  readonly classification: DraftClassification;
  readonly canonicalDraft: RecoverableGenerationDraftV1 | null;
  /** Exact old-form answers only. Never pass this object directly to generation. */
  readonly exactPrefill: Readonly<Record<string, string>>;
  readonly fields: readonly RecoveredField[];
}

/**
 * No unversioned object is called canonical, even if a few field names match.
 * Every populated unknown or ambiguous answer is listed for explicit review.
 */
export function analyseGenerationDraft(
  input: unknown,
  documentType: RecoveryDocumentType,
): GenerationDraftAnalysis {
  const data = record(input);
  if (!data) return { classification: "unknown", canonicalDraft: null, exactPrefill: {}, fields: [] };

  const hasEnvelope = "generationDraftVersion" in data || "documentType" in data || "answers" in data;
  if (hasEnvelope) {
    const candidate = RecoverableGenerationDraftV1Schema.safeParse(data);
    if (candidate.success && candidate.data.documentType === documentType) {
      return { classification: "canonical", canonicalDraft: candidate.data, exactPrefill: {}, fields: [] };
    }
    const knownVersion = data.generationDraftVersion === GENERATION_DRAFT_VERSION;
    const knownType = data.documentType === documentType;
    const fields = Object.entries(data)
      .filter(([key]) => key !== "generationDraftVersion" && key !== "documentType")
      .flatMap(([key, value]) => populatedFieldPaths(value, `/${pointerSegment(key)}`))
      .map((sourceField) => ({
        sourceField, disposition: "review-required" as const,
      }));
    return { classification: knownVersion && knownType ? "mixed" : "unknown",
      canonicalDraft: null, exactPrefill: {}, fields };
  }

  const recognised = documentType === "COSHH" ? [] : legacyKeys[documentType];
  const recognisedSet = new Set(recognised);
  const populatedKeys = Object.keys(data).filter((key) => populated(data[key]));
  const hasLegacyAnswer = populatedKeys.some((key) => recognisedSet.has(key));
  const hasUnknownAnswer = populatedKeys.some((key) => !recognisedSet.has(key));
  const classification: DraftClassification = !hasLegacyAnswer ? "unknown"
    : hasUnknownAnswer ? "mixed" : "legacy";
  const mappings = exactLegacyFields[documentType] ?? {};
  const exactPrefill: Record<string, string> = {};
  const fields = populatedKeys.map((key): RecoveredField => {
    const targetField = mappings[key];
    if (hasLegacyAnswer && targetField && typeof data[key] === "string") {
      exactPrefill[targetField] = data[key];
      return { sourceField: `/${pointerSegment(key)}`, disposition: "exact-prefill", targetField };
    }
    return { sourceField: `/${pointerSegment(key)}`, disposition: "review-required" };
  });
  return { classification, canonicalDraft: null, exactPrefill, fields };
}

/** A user-reviewed exclusion is an explicit action; analysis never creates it. */
export function markFieldAsSavedNotesOnly(
  analysis: GenerationDraftAnalysis,
  sourceField: string,
): GenerationDraftAnalysis {
  if (!analysis.fields.some((field) => field.sourceField === sourceField &&
      field.disposition === "review-required")) {
    throw new Error("Only a review-required field can be excluded to saved notes");
  }
  return {
    ...analysis,
    fields: analysis.fields.map((field) => field.sourceField === sourceField
      ? { sourceField, disposition: "saved-notes-only" as const } : field),
  };
}

/**
 * Call at the final review boundary before constructing a new canonical draft
 * from recovered answers. A malformed/unknown source needs a fresh draft, and
 * every ambiguous populated field needs an explicit reviewed disposition.
 */
export function assertRecoveryReviewed(analysis: GenerationDraftAnalysis): void {
  if (analysis.classification === "unknown") {
    throw new Error("Unknown draft format requires a fresh reviewed draft");
  }
  if (analysis.fields.some((field) => field.disposition === "review-required")) {
    throw new Error("Recovery contains answers requiring review");
  }
}

/** Separate from the wizard draft key: ordinary draft clear cannot erase it. */
export function generationRecoveryKey(args: {
  userId: string; projectId: string; documentType: RecoveryDocumentType;
}): string {
  return `generation-recovery-v1::${JSON.stringify([args.userId, args.projectId, args.documentType])}`;
}

export interface OriginalDraftSnapshot {
  /** Exact local AsyncStorage bytes, including malformed JSON if present. */
  readonly rawJson: string;
  readonly updatedAt: string | null;
}

export interface GenerationRecoveryArchiveV1 {
  readonly recoveryArchiveVersion: 1;
  readonly documentType: RecoveryDocumentType;
  readonly capturedAt: string;
  readonly originalLocal: OriginalDraftSnapshot | null;
  /** JSON serialization of the server answersJson as first received. */
  readonly originalServer: OriginalDraftSnapshot | null;
}

/** First observed original wins independently for local and server sources. */
export function preserveOriginalDraftSources(args: {
  documentType: RecoveryDocumentType;
  capturedAt: string;
  previous?: GenerationRecoveryArchiveV1 | null;
  local?: OriginalDraftSnapshot | null;
  server?: { answersJson: unknown; updatedAt: string | null } | null;
}): GenerationRecoveryArchiveV1 {
  const previous = args.previous;
  if (previous && previous.documentType !== args.documentType) {
    throw new Error("Recovery archive belongs to a different document type");
  }
  const localSource = previous?.originalLocal ?? args.local;
  const originalLocal = localSource
    ? Object.freeze({ rawJson: localSource.rawJson, updatedAt: localSource.updatedAt }) : null;
  const serverJson = args.server ? JSON.stringify(args.server.answersJson) : undefined;
  const serverSource = previous?.originalServer ?? (serverJson === undefined ? null
    : { rawJson: serverJson, updatedAt: args.server?.updatedAt ?? null });
  const originalServer = serverSource
    ? Object.freeze({ rawJson: serverSource.rawJson, updatedAt: serverSource.updatedAt }) : null;
  return Object.freeze({
    recoveryArchiveVersion: 1 as const,
    documentType: args.documentType,
    capturedAt: previous?.capturedAt ?? args.capturedAt,
    originalLocal,
    originalServer,
  });
}
